import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CalendarDays,
  Loader2,
  MapPin,
  Megaphone,
  Music,
  Plus,
  Tent,
  Trash2,
  Wallet,
  X,
} from 'lucide-react';
import {
  creerFestivalArtiste,
  creerFestivalEdition,
  creerFestivalPassage,
  creerFestivalZone,
  fetchFestival,
  fetchFestivalEngagements,
  fetchFestivalProgramme,
  modifierFestivalZone,
  poserFestivalEngagement,
  supprimerFestivalArtiste,
  supprimerFestivalPassage,
  supprimerFestivalZone,
  transitionFestivalPassage,
  type FestivalEdition,
  type FestivalPassage,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';

/**
 * Le festival : éditions, zones, line-up, grille, contrats.
 *
 * LA SAISIE EST UN FORMULAIRE, PAS UN GLISSER-DÉPOSER
 * ---------------------------------------------------------------------------
 * La grille se lit ; elle se remplit par un formulaire ordinaire — zone, jour,
 * heure, durée. C'est une exigence d'accessibilité ET une assurance de
 * terrain : la régie corrige un horaire depuis un téléphone sous la pluie, au
 * clavier, sans traîner un bloc sur un écran de six pouces.
 *
 * LES HEURES SE SAISISSENT DANS LE FUSEAU DE L'ÉDITION
 * ---------------------------------------------------------------------------
 * Le serveur stocke l'UTC ; personne ne programme un set « à 19 h UTC ». La
 * conversion se fait ici, dans le fuseau que l'édition déclare — et dans ce
 * sens uniquement : l'affichage repart de l'UTC du serveur.
 *
 * LES CONTRATS SONT UN ONGLET QUI N'EXISTE PAS SANS SON DROIT
 * ---------------------------------------------------------------------------
 * Pas grisé, pas masqué : absent. Le serveur garde la porte de toute façon ;
 * l'écran évite simplement de dessiner une porte que le droit n'ouvre pas.
 */

/** L'instant UTC d'une saisie locale au fuseau donné — par approximation corrigée. */
function versUtc(jour: string, heure: string, fuseau: string): string {
  const voulu = new Date(`${jour}T${heure}:00Z`).getTime();
  let estimation = voulu;

  // Deux passes suffisent : l'écart entre estimation et rendu local est
  // l'offset du fuseau, stable à l'échelle d'une correction (sauf l'instant
  // exact d'un changement d'heure, où la seconde passe rattrape).
  for (let i = 0; i < 2; i++) {
    const rendu = new Date(estimation).toLocaleString('sv-SE', { timeZone: fuseau }).replace(' ', 'T');
    estimation += voulu - new Date(`${rendu}Z`).getTime();
  }

  return new Date(estimation).toISOString();
}

function heureLocale(iso: string | null, fuseau: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: fuseau });
}

const STATUTS: Record<string, { libelle: string; classe: string }> = {
  brouillon: { libelle: 'Brouillon', classe: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200' },
  confirme: { libelle: 'Confirmé', classe: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200' },
  annonce: { libelle: 'Annoncé', classe: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
  annule: { libelle: 'Annulé', classe: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200' },
};

export default function FestivalPage() {
  const { isLoading: authLoading, aLeDroit } = useAuth();
  const queryClient = useQueryClient();

  const peutVoir = aLeDroit('voir_festival');
  const peutGerer = aLeDroit('gerer_festival');
  const peutPublier = aLeDroit('publier_festival');
  const peutVoirContrats = aLeDroit('voir_festival_contrats');
  const peutGererContrats = aLeDroit('gerer_festival_contrats');

  const [editionId, setEditionId] = useState<number | null>(null);
  const [notice, setNotice] = useState<{ type: 'ok' | 'erreur' | 'attention'; texte: string } | null>(null);

  /*
   * L'onglet vit dans l'URL, pas dans un etat local.
   *
   * Le menu lateral porte les quatre sous-entrees — Programmation, Zones,
   * Artistes, Contrats — et chacune doit ouvrir SON ecran, se partager en
   * lien, et survivre a un rechargement. Un etat local ferait mentir le menu :
   * l'entree surlignee ne serait pas l'onglet affiche.
   */
  const navigate = useNavigate();
  const { onglet: ongletUrl } = useParams();
  const ONGLETS = ['programmation', 'zones', 'artistes', 'contrats'] as const;
  type Onglet = (typeof ONGLETS)[number];
  const onglet: Onglet = (ONGLETS as readonly string[]).includes(ongletUrl ?? '')
    ? (ongletUrl as Onglet)
    : 'programmation';
  const setOnglet = (o: Onglet) => navigate(`/bureau/festival/${o}`);

  const { data, isPending } = useQuery({ queryKey: ['festival'], queryFn: fetchFestival, enabled: peutVoir });

  const editions = data?.editions ?? [];
  const edition: FestivalEdition | null =
    editions.find((e) => e.id === editionId) ?? editions[0] ?? null;

  const rafraichir = () => {
    queryClient.invalidateQueries({ queryKey: ['festival'] });
    queryClient.invalidateQueries({ queryKey: ['festival-programme'] });
    queryClient.invalidateQueries({ queryKey: ['festival-engagements'] });
  };

  const surErreur = (e: Error) => setNotice({ type: 'erreur', texte: e.message });

  if (authLoading || (peutVoir && isPending)) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!peutVoir) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              Le festival dépend de votre fonction au bureau : un administrateur peut l'ouvrir dans
              la grille de droits.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex items-center gap-3 flex-wrap">
        <Tent className="w-7 h-7 text-primary-700 shrink-0" />
        <div className="flex-1 min-w-[16rem]">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Festival</h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Zones, line-up, grille de programmation et annonces.
          </p>
        </div>
        {editions.length > 0 && (
          <label className="text-sm text-gray-600 dark:text-gray-400">
            Édition{' '}
            <select
              value={edition?.id ?? ''}
              onChange={(e) => setEditionId(Number(e.target.value))}
              className="input"
            >
              {editions.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {notice && (
        <div
          className={`card text-sm flex items-start gap-3 ${
            notice.type === 'ok'
              ? 'bg-green-50 border-green-200 text-green-800'
              : notice.type === 'attention'
                ? 'bg-amber-50 border-amber-200 text-amber-800'
                : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="flex-1 whitespace-pre-wrap">{notice.texte}</p>
          <button onClick={() => setNotice(null)} className="text-xs underline">Fermer</button>
        </div>
      )}

      {edition === null ? (
        <PremiereEdition peutGerer={peutGerer} onFait={rafraichir} onErreur={surErreur} />
      ) : (
        <>
          <nav aria-label="Sections du festival" className="flex gap-2 flex-wrap">
            {(
              [
                ['programmation', 'Programmation', CalendarDays],
                ['zones', 'Zones', MapPin],
                ['artistes', 'Artistes', Music],
                ...(peutVoirContrats ? ([['contrats', 'Contrats', Wallet]] as const) : []),
              ] as const
            ).map(([code, libelle, Icone]) => (
              <button
                key={code}
                type="button"
                onClick={() => setOnglet(code)}
                aria-current={onglet === code ? 'page' : undefined}
                className={`flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm transition-colors ${
                  onglet === code
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200'
                    : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-primary-300'
                }`}
              >
                <Icone className="w-4 h-4" />
                {libelle}
              </button>
            ))}
          </nav>

          {onglet === 'programmation' && (
            <Programmation
              edition={edition}
              artistes={data?.artistes ?? []}
              peutGerer={peutGerer}
              peutPublier={peutPublier}
              onNotice={setNotice}
              onFait={rafraichir}
            />
          )}
          {onglet === 'zones' && (
            <Zones edition={edition} peutGerer={peutGerer} onFait={rafraichir} onErreur={surErreur} />
          )}
          {onglet === 'artistes' && (
            <Artistes artistes={data?.artistes ?? []} peutGerer={peutGerer} onFait={rafraichir} onErreur={surErreur} />
          )}
          {onglet === 'contrats' && peutVoirContrats && (
            <Contrats
              edition={edition}
              artistes={data?.artistes ?? []}
              peutGerer={peutGererContrats}
              onFait={rafraichir}
              onErreur={surErreur}
            />
          )}
        </>
      )}
    </div>
  );
}

/* ----------------------------- Première édition ---------------------------- */

function PremiereEdition({
  peutGerer,
  onFait,
  onErreur,
}: {
  peutGerer: boolean;
  onFait: () => void;
  onErreur: (e: Error) => void;
}) {
  const [nom, setNom] = useState('');
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');

  const creer = useMutation({
    mutationFn: () => creerFestivalEdition({ nom, dateDebut: debut, dateFin: fin }),
    onSuccess: onFait,
    onError: onErreur,
  });

  if (!peutGerer) {
    return <p className="card text-sm text-gray-600 dark:text-gray-400">Aucune édition pour l'instant.</p>;
  }

  return (
    <form
      className="card space-y-3 max-w-xl"
      onSubmit={(e) => {
        e.preventDefault();
        creer.mutate();
      }}
    >
      <h2 className="font-semibold text-gray-900 dark:text-gray-100">La première édition</h2>
      <label className="block text-sm text-gray-600 dark:text-gray-400">
        Nom
        <input required value={nom} onChange={(e) => setNom(e.target.value)}
          placeholder="Festival des Cigales 2027" className="input w-full mt-0.5" />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-sm text-gray-600 dark:text-gray-400">
          Premier jour
          <input required type="date" value={debut} onChange={(e) => setDebut(e.target.value)} className="input w-full mt-0.5" />
        </label>
        <label className="block text-sm text-gray-600 dark:text-gray-400">
          Dernier jour
          <input required type="date" value={fin} onChange={(e) => setFin(e.target.value)} className="input w-full mt-0.5" />
        </label>
      </div>
      <button type="submit" disabled={creer.isPending} className="btn-primary flex items-center gap-2">
        <Plus className="w-4 h-4" />
        Créer l'édition
      </button>
    </form>
  );
}

/* ------------------------------- Programmation ----------------------------- */

function Programmation({
  edition,
  artistes,
  peutGerer,
  peutPublier,
  onNotice,
  onFait,
}: {
  edition: FestivalEdition;
  artistes: { id: number; nom: string }[];
  peutGerer: boolean;
  peutPublier: boolean;
  onNotice: (n: { type: 'ok' | 'erreur' | 'attention'; texte: string }) => void;
  onFait: () => void;
}) {
  const queryClient = useQueryClient();
  const scenes = edition.zones.filter((z) => z.scene);

  const [zone, setZone] = useState<number | ''>('');
  const [artiste, setArtiste] = useState<number | ''>('');
  const [titre, setTitre] = useState('');
  const [type, setType] = useState('set');
  const [jour, setJour] = useState(edition.dateDebut ?? '');
  const [heure, setHeure] = useState('21:00');
  const [duree, setDuree] = useState(60);

  const { data } = useQuery({
    queryKey: ['festival-programme', edition.id],
    queryFn: () => fetchFestivalProgramme(edition.id),
  });

  const rafraichir = () => {
    queryClient.invalidateQueries({ queryKey: ['festival-programme', edition.id] });
    onFait();
  };

  const poser = useMutation({
    mutationFn: () => {
      const debutUtc = versUtc(jour, heure, edition.fuseau);
      const finUtc = new Date(new Date(debutUtc).getTime() + duree * 60_000).toISOString();

      return creerFestivalPassage(edition.id, {
        zone,
        artiste: artiste === '' ? null : artiste,
        titreAffiche: titre || null,
        type,
        debut: debutUtc,
        fin: finUtc,
      });
    },
    onSuccess: (r: { avertissements?: { message: string }[] }) => {
      rafraichir();
      const avertissements = r.avertissements ?? [];
      onNotice(
        avertissements.length > 0
          ? { type: 'attention', texte: 'Posé, avec réserve :\n' + avertissements.map((a) => `• ${a.message}`).join('\n') }
          : { type: 'ok', texte: 'Créneau posé.' }
      );
    },
    onError: (e: Error) => onNotice({ type: 'erreur', texte: e.message }),
  });

  const transiter = useMutation({
    mutationFn: ({ id, transition }: { id: number; transition: 'confirmer' | 'revenir' | 'annoncer' | 'annuler' }) =>
      transitionFestivalPassage(id, transition),
    onSuccess: rafraichir,
    onError: (e: Error) => onNotice({ type: 'erreur', texte: e.message }),
  });

  const supprimer = useMutation({
    mutationFn: (id: number) => supprimerFestivalPassage(id),
    onSuccess: rafraichir,
    onError: (e: Error) => onNotice({ type: 'erreur', texte: e.message }),
  });

  // Groupés par journée FESTIVAL — celle du serveur, jamais la date civile :
  // le set de 01 h 30 doit rester sous « vendredi ».
  const parJour = useMemo(() => {
    const groupes = new Map<string, FestivalPassage[]>();
    for (const passage of data?.passages ?? []) {
      const cle = passage.jourFestival ?? '?';
      groupes.set(cle, [...(groupes.get(cle) ?? []), passage]);
    }
    return [...groupes.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  return (
    <div className="space-y-4">
      {peutGerer && (
        <form
          className="card grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            poser.mutate();
          }}
        >
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Scène
            <select required value={zone} onChange={(e) => setZone(Number(e.target.value))} className="input w-full mt-0.5">
              <option value="">Choisir…</option>
              {scenes.map((z) => (
                <option key={z.id} value={z.id}>{z.libelle}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Artiste
            <select value={artiste} onChange={(e) => setArtiste(e.target.value === '' ? '' : Number(e.target.value))} className="input w-full mt-0.5">
              <option value="">— sans artiste —</option>
              {artistes.map((a) => (
                <option key={a.id} value={a.id}>{a.nom}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Titre libre (si pas d'artiste)
            <input value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Secret guest" className="input w-full mt-0.5" />
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Type
            <select value={type} onChange={(e) => setType(e.target.value)} className="input w-full mt-0.5">
              <option value="set">Set</option>
              <option value="dj_set">DJ set</option>
              <option value="balance">Balance (interne)</option>
              <option value="plateau">Changement de plateau</option>
              <option value="animation">Animation</option>
            </select>
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Jour
            <input required type="date" value={jour} onChange={(e) => setJour(e.target.value)} className="input w-full mt-0.5" />
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Heure ({edition.fuseau})
            <input required type="time" value={heure} onChange={(e) => setHeure(e.target.value)} className="input w-full mt-0.5" />
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Durée (minutes)
            <input required type="number" min={5} value={duree} onChange={(e) => setDuree(Number(e.target.value))} className="input w-full mt-0.5" />
          </label>
          <div className="flex items-end">
            <button type="submit" disabled={poser.isPending} className="btn-primary flex items-center gap-2 w-full justify-center">
              {poser.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Poser le créneau
            </button>
          </div>
        </form>
      )}

      {parJour.length === 0 && (
        <p className="card text-sm text-gray-600 dark:text-gray-400">Aucun créneau : la grille est vide.</p>
      )}

      {parJour.map(([jourFestival, passages]) => (
        <section key={jourFestival} className="card space-y-3" aria-labelledby={`jour-${jourFestival}`}>
          <h2 id={`jour-${jourFestival}`} className="font-semibold text-gray-900 dark:text-gray-100 capitalize">
            {new Date(`${jourFestival}T12:00:00Z`).toLocaleDateString('fr-FR', {
              weekday: 'long', day: 'numeric', month: 'long',
            })}
            <span className="text-xs font-normal text-gray-500 dark:text-gray-400"> — la soirée court jusqu'à {edition.heureBascule} h</span>
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <th className="py-1.5 pr-3">Horaire</th>
                  <th className="py-1.5 pr-3">Scène</th>
                  <th className="py-1.5 pr-3">Qui</th>
                  <th className="py-1.5 pr-3">Statut</th>
                  <th className="py-1.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {passages.map((p) => {
                  const statut = STATUTS[p.statut] ?? STATUTS.brouillon;
                  return (
                    <tr key={p.id} className={`border-t border-gray-100 dark:border-gray-700 ${p.statut === 'annule' ? 'opacity-60' : ''}`}>
                      <td className="py-2 pr-3 tabular-nums whitespace-nowrap">
                        {heureLocale(p.debut, edition.fuseau)} – {heureLocale(p.fin, edition.fuseau)}
                      </td>
                      <td className="py-2 pr-3">{p.zoneCode}</td>
                      <td className={`py-2 pr-3 font-medium text-gray-900 dark:text-gray-100 ${p.statut === 'annule' ? 'line-through' : ''}`}>
                        {p.artisteNom ?? p.titreAffiche ?? <span className="text-gray-400">({p.type})</span>}
                        {p.type !== 'set' && p.type !== 'dj_set' && (
                          <span className="ml-1 text-xs text-gray-500">· {p.type}</span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs ${statut.classe}`}>{statut.libelle}</span>
                      </td>
                      <td className="py-2">
                        <div className="flex gap-1.5 flex-wrap">
                          {peutGerer && p.statut === 'brouillon' && (
                            <>
                              <BoutonAction onClick={() => transiter.mutate({ id: p.id, transition: 'confirmer' })}>Confirmer</BoutonAction>
                              <button
                                type="button"
                                onClick={() => supprimer.mutate(p.id)}
                                aria-label="Supprimer ce brouillon"
                                className="p-1 text-gray-400 hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {peutGerer && p.statut === 'confirme' && (
                            <BoutonAction onClick={() => transiter.mutate({ id: p.id, transition: 'revenir' })}>Brouillon</BoutonAction>
                          )}
                          {peutPublier && p.statut === 'confirme' && (
                            <BoutonAction accent onClick={() => transiter.mutate({ id: p.id, transition: 'annoncer' })}>
                              <Megaphone className="w-3.5 h-3.5" /> Annoncer
                            </BoutonAction>
                          )}
                          {peutPublier && p.statut !== 'annule' && p.statut !== 'brouillon' && (
                            <BoutonAction onClick={() => transiter.mutate({ id: p.id, transition: 'annuler' })}>
                              <X className="w-3.5 h-3.5" /> Annuler
                            </BoutonAction>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}

function BoutonAction({
  children,
  onClick,
  accent = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  accent?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs transition-colors ${
        accent
          ? 'border-primary-500 text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20'
          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-primary-300'
      }`}
    >
      {children}
    </button>
  );
}

/* ---------------------------------- Zones ---------------------------------- */

function Zones({
  edition,
  peutGerer,
  onFait,
  onErreur,
}: {
  edition: FestivalEdition;
  peutGerer: boolean;
  onFait: () => void;
  onErreur: (e: Error) => void;
}) {
  const [code, setCode] = useState('');
  const [libelle, setLibelle] = useState('');
  const [type, setType] = useState('scene');

  const creer = useMutation({
    mutationFn: () => creerFestivalZone(edition.id, { code, libelle, type }),
    onSuccess: () => {
      setCode('');
      setLibelle('');
      onFait();
    },
    onError: onErreur,
  });

  const retirer = useMutation({
    mutationFn: (id: number) => supprimerFestivalZone(id),
    onSuccess: onFait,
    onError: onErreur,
  });

  const regler = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) => modifierFestivalZone(id, payload),
    onSuccess: onFait,
    onError: onErreur,
  });

  return (
    <div className="space-y-4">
      {peutGerer && (
        <form
          className="card grid gap-3 sm:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            creer.mutate();
          }}
        >
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Code court
            <input required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="GS" maxLength={16} className="input w-full mt-0.5 font-mono" />
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400 sm:col-span-2">
            Libellé
            <input required value={libelle} onChange={(e) => setLibelle(e.target.value)}
              placeholder="Grande Scène" className="input w-full mt-0.5" />
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Type
            <select value={type} onChange={(e) => setType(e.target.value)} className="input w-full mt-0.5">
              <option value="scene">Scène</option>
              <option value="bar">Bar</option>
              <option value="restauration">Restauration</option>
              <option value="camping">Camping</option>
              <option value="parking">Parking</option>
              <option value="loges">Loges</option>
              <option value="secours">Poste de secours</option>
              <option value="entree">Entrée</option>
              <option value="accueil">Accueil</option>
              <option value="autre">Autre</option>
            </select>
          </label>
          <button type="submit" disabled={creer.isPending} className="btn-primary flex items-center gap-2 sm:col-span-4 w-fit">
            <Plus className="w-4 h-4" /> Ajouter la zone
          </button>
        </form>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <th className="py-1.5 pr-3">Code</th>
              <th className="py-1.5 pr-3">Libellé</th>
              <th className="py-1.5 pr-3">Type</th>
              <th className="py-1.5 pr-3">Jauge</th>
              <th className="py-1.5 pr-3">Plateau (min)</th>
              <th className="py-1.5 pr-3">Public</th>
              {peutGerer && <th className="py-1.5">Retirer</th>}
            </tr>
          </thead>
          <tbody>
            {edition.zones.map((z) => (
              <tr key={z.id} className="border-t border-gray-100 dark:border-gray-700">
                <td className="py-2 pr-3 font-mono">{z.code}</td>
                <td className="py-2 pr-3 font-medium text-gray-900 dark:text-gray-100">{z.libelle}</td>
                <td className="py-2 pr-3">{z.type}</td>
                <td className="py-2 pr-3">
                  {peutGerer ? (
                    <input
                      type="number"
                      defaultValue={z.capacite ?? ''}
                      aria-label={`Jauge de ${z.libelle}`}
                      onBlur={(e) =>
                        regler.mutate({ id: z.id, payload: { capacite: e.target.value === '' ? null : Number(e.target.value) } })
                      }
                      className="input w-24"
                    />
                  ) : (
                    (z.capacite ?? '—')
                  )}
                </td>
                <td className="py-2 pr-3">
                  {peutGerer && z.scene ? (
                    <input
                      type="number"
                      defaultValue={z.minutesPlateau}
                      aria-label={`Minutes de plateau de ${z.libelle}`}
                      onBlur={(e) => regler.mutate({ id: z.id, payload: { minutesPlateau: Number(e.target.value) } })}
                      className="input w-20"
                    />
                  ) : z.scene ? (
                    z.minutesPlateau
                  ) : (
                    '—'
                  )}
                </td>
                <td className="py-2 pr-3">{z.visiblePublic ? 'oui' : 'non'}</td>
                {peutGerer && (
                  <td className="py-2">
                    <button
                      type="button"
                      onClick={() => retirer.mutate(z.id)}
                      aria-label={`Retirer ${z.libelle}`}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {edition.zones.length === 0 && (
              <tr>
                <td colSpan={7} className="py-4 text-center text-gray-500">Aucune zone.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------------------------------- Artistes -------------------------------- */

function Artistes({
  artistes,
  peutGerer,
  onFait,
  onErreur,
}: {
  artistes: { id: number; nom: string; genres: string | null; pays: string | null; teteAffiche: boolean }[];
  peutGerer: boolean;
  onFait: () => void;
  onErreur: (e: Error) => void;
}) {
  const [nom, setNom] = useState('');
  const [genres, setGenres] = useState('');
  const [teteAffiche, setTeteAffiche] = useState(false);

  const creer = useMutation({
    mutationFn: () => creerFestivalArtiste({ nom, genres: genres || null, teteAffiche }),
    onSuccess: () => {
      setNom('');
      setGenres('');
      setTeteAffiche(false);
      onFait();
    },
    onError: onErreur,
  });

  const retirer = useMutation({
    mutationFn: (id: number) => supprimerFestivalArtiste(id),
    onSuccess: onFait,
    onError: onErreur,
  });

  return (
    <div className="space-y-4">
      {peutGerer && (
        <form
          className="card grid gap-3 sm:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            creer.mutate();
          }}
        >
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Nom
            <input required value={nom} onChange={(e) => setNom(e.target.value)} className="input w-full mt-0.5" />
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Genres
            <input value={genres} onChange={(e) => setGenres(e.target.value)} placeholder="rock, post-punk" className="input w-full mt-0.5" />
          </label>
          <label className="flex items-end gap-2 text-sm text-gray-800 dark:text-gray-200 pb-2">
            <input type="checkbox" checked={teteAffiche} onChange={(e) => setTeteAffiche(e.target.checked)} />
            Tête d'affiche
          </label>
          <button type="submit" disabled={creer.isPending} className="btn-primary flex items-center gap-2 w-fit">
            <Plus className="w-4 h-4" /> Ajouter
          </button>
        </form>
      )}

      <ul className="card divide-y divide-gray-100 dark:divide-gray-700">
        {artistes.map((a) => (
          <li key={a.id} className="py-2 flex items-center gap-3">
            <span className="font-medium text-gray-900 dark:text-gray-100">{a.nom}</span>
            {a.teteAffiche && (
              <span className="rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-200 px-2 py-0.5 text-xs">
                tête d'affiche
              </span>
            )}
            <span className="text-sm text-gray-500 dark:text-gray-400">{a.genres}</span>
            {peutGerer && (
              <button
                type="button"
                onClick={() => retirer.mutate(a.id)}
                aria-label={`Retirer ${a.nom}`}
                className="ml-auto p-1 text-gray-400 hover:text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </li>
        ))}
        {artistes.length === 0 && <li className="py-3 text-sm text-gray-500">Aucun artiste — l'import CSV existe aussi : festival:artistes:importer.</li>}
      </ul>
    </div>
  );
}

/* --------------------------------- Contrats -------------------------------- */

function Contrats({
  edition,
  artistes,
  peutGerer,
  onFait,
  onErreur,
}: {
  edition: FestivalEdition;
  artistes: { id: number; nom: string }[];
  peutGerer: boolean;
  onFait: () => void;
  onErreur: (e: Error) => void;
}) {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['festival-engagements', edition.id],
    queryFn: () => fetchFestivalEngagements(edition.id),
  });

  const [artiste, setArtiste] = useState<number | ''>('');
  const [cachet, setCachet] = useState('');
  const [statut, setStatut] = useState('a_negocier');

  const poser = useMutation({
    mutationFn: () =>
      poserFestivalEngagement(edition.id, {
        artiste,
        statutContrat: statut,
        cachetCentimes: cachet === '' ? null : Math.round(Number(cachet) * 100),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['festival-engagements', edition.id] });
      onFait();
    },
    onError: onErreur,
  });

  return (
    <div className="space-y-4">
      <p className="card bg-amber-50 border-amber-200 text-amber-800 text-sm">
        Les cachets sont la donnée la plus confidentielle de l'association. Chaque consultation de
        cet onglet est journalisée.
      </p>

      {peutGerer && (
        <form
          className="card grid gap-3 sm:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            poser.mutate();
          }}
        >
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Artiste
            <select required value={artiste} onChange={(e) => setArtiste(Number(e.target.value))} className="input w-full mt-0.5">
              <option value="">Choisir…</option>
              {artistes.map((a) => (
                <option key={a.id} value={a.id}>{a.nom}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Cachet (€)
            <input type="number" step="0.01" min="0" value={cachet} onChange={(e) => setCachet(e.target.value)} className="input w-full mt-0.5" />
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Contrat
            <select value={statut} onChange={(e) => setStatut(e.target.value)} className="input w-full mt-0.5">
              <option value="a_negocier">À négocier</option>
              <option value="envoye">Envoyé</option>
              <option value="signe">Signé</option>
              <option value="annule">Annulé</option>
            </select>
          </label>
          <div className="flex items-end">
            <button type="submit" disabled={poser.isPending} className="btn-primary w-full justify-center flex items-center gap-2">
              <Plus className="w-4 h-4" /> Enregistrer
            </button>
          </div>
        </form>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <th className="py-1.5 pr-3">Artiste</th>
              <th className="py-1.5 pr-3">Cachet</th>
              <th className="py-1.5 pr-3">Contrat</th>
              <th className="py-1.5 pr-3">Signé le</th>
              <th className="py-1.5">GUSO</th>
            </tr>
          </thead>
          <tbody>
            {(data?.engagements ?? []).map((g) => (
              <tr key={g.id} className="border-t border-gray-100 dark:border-gray-700">
                <td className="py-2 pr-3 font-medium text-gray-900 dark:text-gray-100">{g.artisteNom}</td>
                <td className="py-2 pr-3 tabular-nums">
                  {g.cachetCentimes !== null ? `${(g.cachetCentimes / 100).toLocaleString('fr-FR')} €` : '—'}
                </td>
                <td className="py-2 pr-3">{g.statutContrat.replace('_', ' ')}</td>
                <td className="py-2 pr-3">{g.contratSigneLe ?? '—'}</td>
                <td className="py-2">{g.referenceGuso ?? '—'}</td>
              </tr>
            ))}
            {(data?.engagements ?? []).length === 0 && (
              <tr>
                <td colSpan={5} className="py-4 text-center text-gray-500">Aucun engagement.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
